// 🎓 Lecture Preview Page - LocalStorage-based Draft Preview
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from '../../src/hooks/useI18nNavigate';
import {
  course_lecture_lecturePage_courseLoading,
  course_lecture_lecturePage_noLecture,
} from '@paraglide/messages';

import LectureTemplate from '../template/LectureTemplate';
import { useCurriculumStore } from '@edusense/entities-course';
import { useGetCourseDraftById } from '@edusense/course-client';
import type { FlatLecture } from './LecturePage.types';

interface LecturePreviewPageProps {
  courseId?: string;
  sectionIndex?: string;
  lectureIndex?: string;
}

const LecturePreviewPage: React.FC<LecturePreviewPageProps> = (props) => {
  const [isTocVisible, setIsTocVisible] = useState<boolean>(false);
  const navigate = useNavigate();

  // Get params from router or props
  let routerParams: { courseId?: string; sectionIndex?: string; lectureIndex?: string } = {};
  try {
    routerParams = useParams<{ courseId: string; sectionIndex: string; lectureIndex: string }>() || {};
  } catch (e) {
    // In Next.js environment, useParams won't exist
  }

  const courseId = props.courseId || routerParams?.courseId;
  const sectionIndexStr = props.sectionIndex || routerParams?.sectionIndex;
  const lectureIndexStr = props.lectureIndex || routerParams?.lectureIndex;

  const sectionIndex = sectionIndexStr ? parseInt(sectionIndexStr, 10) : -1;
  const lectureIndex = lectureIndexStr ? parseInt(lectureIndexStr, 10) : -1;

  console.log('🔍 [LecturePreviewPage] Params:', {
    courseId,
    sectionIndex,
    lectureIndex,
  });

  // Get local editing state from CurriculumStore
  const { sections, title: courseTitle, initializeSections } = useCurriculumStore();

  // Fetch draft data to initialize CurriculumStore if empty
  const { data: draftData, isLoading: isDraftLoading } = useGetCourseDraftById(
    courseId!,
    {
      query: {
        enabled: !!courseId && (!sections || sections.length === 0),
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        retry: 1,
      },
    },
  );

  // Initialize CurriculumStore from Draft API if empty
  React.useEffect(() => {
    if (draftData && (!sections || sections.length === 0) && courseId) {
      console.log('🔄 [Preview] Initializing CurriculumStore from Draft API');

      const mappedSections = draftData.sections?.map((section, idx) => ({
        id: section.id?.toString() || `temp-${idx}`,
        title: section.title || '',
        order: section.order ?? idx + 1,
        lessons: section.lectures?.map((lecture, lecIdx) => ({
          id: lecture.id?.toString() || `temp-${idx}-${lecIdx}`,
          title: lecture.title || '',
          markdown: lecture.markdownContent || undefined,
          videoUrl: lecture.videoUrl || undefined,
          order: lecture.order ?? lecIdx + 1,
          contentType: lecture.contentType,
          duration: lecture.duration ?? undefined,
        })) || [],
      })) || [];

      initializeSections(mappedSections, courseId, { markAsDirtyOnImport: false });
      console.log('✅ [Preview] CurriculumStore initialized with', mappedSections.length, 'sections');
    }
  }, [draftData, sections, courseId, initializeSections]);

  // Extract lecture by index from local state
  const lectureDetail = useMemo(() => {
    if (!sections || sectionIndex < 0 || lectureIndex < 0) {
      console.log('🔍 [Preview] Invalid indices or no sections:', {
        sectionIndex,
        lectureIndex,
        hasSections: !!sections,
        sectionsLength: sections?.length,
      });
      return null;
    }

    const section = sections[sectionIndex];
    if (!section) {
      console.error(`❌ [Preview] Section ${sectionIndex} not found. Total sections: ${sections.length}`);
      return null;
    }

    const lesson = section.lessons?.[lectureIndex];
    if (!lesson) {
      console.error(`❌ [Preview] Lesson ${lectureIndex} not found in section ${sectionIndex}. Total lessons: ${section.lessons?.length}`);
      return null;
    }

    console.log('✅ [Preview] Found lesson by index from CurriculumStore:', {
      sectionIndex,
      lectureIndex,
      lessonId: lesson.id,
      lessonTitle: lesson.title,
      hasMarkdown: !!lesson.markdown,
      markdownLength: lesson.markdown?.length,
    });

    return lesson;
  }, [sections, sectionIndex, lectureIndex]);

  // Convert local sections to CourseDetail-like format
  const courseData = useMemo(() => {
    if (!sections || !courseId) return null;

    return {
      summary: {
        id: courseId,
        title: courseTitle || 'Draft Course',
        owned: true,
        progressRate: 0,
      },
      sections: sections.map((section, idx) => ({
        id: section.id,
        title: section.title,
        order: section.order ?? idx + 1,
        lectures: section.lessons?.map((lesson, lessonIdx) => ({
          id: lesson.id,
          title: lesson.title,
          order: lesson.order ?? lessonIdx + 1,
          contentType: lesson.contentType || 'MARKDOWN',
          duration: lesson.duration,
        })),
      })),
    };
  }, [sections, courseId, courseTitle]);

  // Build flat lectures list for navigation
  const flatLectures: FlatLecture[] = useMemo(() => {
    if (!courseData?.sections) return [];

    const lectures: FlatLecture[] = [];
    courseData.sections.forEach((section, secIdx) => {
      section.lectures?.forEach((lecture, lecIdx) => {
        lectures.push({
          id: `${secIdx}-${lecIdx}`, // Use index as ID for preview
          title: lecture.title || '제목 없음',
          sectionId: section.id?.toString(),
          sectionTitle: section.title || '섹션 제목 없음',
          sectionIndex: secIdx,
          lectureIndex: lecIdx,
        });
      });
    });
    return lectures;
  }, [courseData]);

  // Calculate current lecture index in flat list
  const currentFlatIndex = useMemo(() => {
    if (sectionIndex < 0 || lectureIndex < 0 || !courseData?.sections) return -1;

    let count = 0;
    for (let s = 0; s < courseData.sections.length; s++) {
      const section = courseData.sections[s];
      if (s === sectionIndex) {
        return count + lectureIndex;
      }
      count += section.lectures?.length || 0;
    }
    return -1;
  }, [courseData, sectionIndex, lectureIndex]);

  const hasPreviousLecture = currentFlatIndex > 0;
  const hasNextLecture = currentFlatIndex !== -1 && currentFlatIndex < flatLectures.length - 1;

  // Navigation handlers
  const handleGoBack = () => {
    if (courseId) {
      navigate(`/learn/course/${courseId}/curriculum`);
    } else {
      navigate('.');
    }
  };

  const handleNavigateLecture = (direction: 'previous' | 'next') => {
    if (currentFlatIndex < 0 || !flatLectures.length) return;

    const targetIndex = direction === 'previous' ? currentFlatIndex - 1 : currentFlatIndex + 1;
    if (targetIndex >= 0 && targetIndex < flatLectures.length) {
      const targetLecture = flatLectures[targetIndex];
      if (targetLecture.sectionIndex !== undefined && targetLecture.lectureIndex !== undefined && courseId) {
        navigate(`/learn/course/${courseId}/preview/${targetLecture.sectionIndex}/${targetLecture.lectureIndex}`);
      }
    }
  };

  const handleCompleteCourse = () => {
    // No-op for preview mode
    console.log('Complete course not available in preview mode');
  };

  const handleReviewSubmitSuccess = () => {
    // No-op for preview mode
  };

  const handleQuizSubmit = () => {
    // No-op for preview mode
  };

  // Loading state: either fetching draft or sections not yet loaded
  if (isDraftLoading || !sections || sections.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        {course_lecture_lecturePage_courseLoading()}
      </div>
    );
  }

  // No lecture found
  if (!lectureDetail) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        {course_lecture_lecturePage_noLecture()}
        <div style={{ marginTop: '1rem' }}>
          <button onClick={handleGoBack}>돌아가기</button>
        </div>
      </div>
    );
  }

  // Build lecture object for LectureTemplate
  const lecturePropsForTemplate = {
    id: lectureDetail.id || `${sectionIndex}-${lectureIndex}`,
    title: lectureDetail.title || '제목 없음',
    videoUrl: lectureDetail.videoUrl || undefined,
    description: undefined,
    markdownContent: lectureDetail.markdown || undefined, // ✅ Use 'markdown' from CurriculumStore
    contentType: (lectureDetail.contentType as any) || 'MARKDOWN',
  };

  // Check if last lecture
  const isLastLecture = !hasNextLecture;

  // Build template props
  const templateProps = {
    courseId: courseId || '',
    currentLectureId: lectureDetail.id || `${sectionIndex}-${lectureIndex}`,
    lecture: lecturePropsForTemplate,
    curriculum: courseData?.sections || [],
    isTocVisible,
    onToggleToc: () => setIsTocVisible(!isTocVisible),
    onGoBack: handleGoBack,
    onNavigatePrevious: hasPreviousLecture ? () => handleNavigateLecture('previous') : undefined,
    onNavigateNext: hasNextLecture ? () => handleNavigateLecture('next') : undefined,
    onCompleteCourse: isLastLecture ? handleCompleteCourse : undefined,
    isPreviousEnabled: hasPreviousLecture,
    isNextEnabled: hasNextLecture,
    isLastLecture: isLastLecture,
    onQuizSubmit: handleQuizSubmit,
  };

  return (
    <>
      <LectureTemplate {...templateProps} />
    </>
  );
};

export default LecturePreviewPage;
